import EnumDataSource from "../utils/enum-data-source.util";

const sex = {
  M: "Male",
  F: "Female"
};

// BAS = Birth assigned Sex
export const BAS = new EnumDataSource(sex);

const gender = {
  M: "Man",
  W: "Woman",
  NB: "Non Binary/Other",
  NS: "Not Specified"
};

// Gender options
export const GENDER = new EnumDataSource(gender);

const pronouns = {
  SH: "She/her/hers",
  HH: "He/him/his",
  TH: "They/them/theirs",
  ST: "She/they",
  HT: "He/they"
};

export const PRONOUNS = new EnumDataSource(pronouns);

// SN-19749. BCP-47 / ISO-639 codes, mirroring the backend's `PreferredLanguage` choices.
// `ase` is American Sign Language; `other` pairs with the free-text `preferred_language_other`.
const preferredLanguages = {
  en: "English",
  es: "Spanish",
  zh: "Chinese",
  vi: "Vietnamese",
  tl: "Tagalog",
  ko: "Korean",
  ar: "Arabic",
  ru: "Russian",
  fr: "French",
  ht: "Haitian Creole",
  pt: "Portuguese",
  de: "German",
  it: "Italian",
  pl: "Polish",
  hi: "Hindi",
  ja: "Japanese",
  fa: "Persian",
  ase: "American Sign Language",
  other: "Other"
};

export const PREFERRED_LANGUAGE = new EnumDataSource(preferredLanguages);
export const PREFERRED_LANGUAGE_OTHER = "other";

const maritalStatuses = {
  "1": "Single",
  "2": "Married",
  "3": "Divorced",
  "4": "Domestic Partner",
  "5": "Legally Separated",
  "6": "Widowed",
  "99": "Other"
};

export const MARITAL_STATUSES = new EnumDataSource(maritalStatuses);

const employmentStatus = {
  "1": "Full Time",
  "2": "Part Time",
  "3": "Self Employed",
  "4": "Not Employed",
  "5": "Retired",
  "6": "Active Military",
  "7": "Disabled",
  "8": "Student"
};

export const EMPLOYMENT_STATUSES = new EnumDataSource(employmentStatus);

export const CONTACT_RELATIONSHIP_SELF = "0";
export const CONTACT_RELATIONSHIP_OTHER = "99";

export const contactRelationship = {
  [CONTACT_RELATIONSHIP_SELF]: "Self",
  "1": "Spouse",
  "2": "Sibling",
  "3": "Parent",
  "4": "Child",
  "5": "Partner",
  "6": "Caregiver",
  "7": "Friend",
  "8": "Grandchild",
  "9": "Guardian",
  "10": "Relative",
  "11": "Grandparent",
  [CONTACT_RELATIONSHIP_OTHER]: "Other"
};

export const CONTACT_RELATIONSHIP = new EnumDataSource(contactRelationship, {
  [CONTACT_RELATIONSHIP_SELF]: 1,
  "6": 2, // Caregiver
  "4": 3, // Child
  "7": 4, // Friend
  "8": 5, // Grandchild
  "11": 6, // Grandparent
  "9": 7, // Guardian
  "10": 8, // Relative
  "5": 9, // Partner
  "3": 10, // Parent
  "2": 11, // Sibling
  "1": 12, // Spouse
  [CONTACT_RELATIONSHIP_OTHER]: 13
});

const referralSource = {
  "1": "Current Patient",
  "2": "Physician",
  "3": "Walk In",
  "4": "Practice Website",
  "5": "Google Search",
  "6": "Newsletter",
  "7": "Direct Mail",
  "8": "Radio",
  "99": "Other"
};

export const REFERRAL_SOURCE = new EnumDataSource(referralSource);

export const PRIVATE_PAY_PAYMENT_SOURCE = 1;
export const INSURANCE_PAYMENT_SOURCE = 2;
export const MANAGED_CARE_PAYMENT_SOURCE = 3;
const paymentSourceType = {
  [PRIVATE_PAY_PAYMENT_SOURCE]: "Private Pay",
  [INSURANCE_PAYMENT_SOURCE]: "Insurance",
  [MANAGED_CARE_PAYMENT_SOURCE]: "Managed Care"
};

export const PAYMENT_SOURCE_TYPE = new EnumDataSource(paymentSourceType);

// ENG-1741/SN-19410: whether an invoice with this payment source type can ever have insurer credit
// applied to it — private-pay has no insurer to hold credit for, and managed-care and insurer
// credit never combine (SN-19331). Shared by the Post Payment method dropdown (D13) and the
// Insurance Payments/Adjustments form's own gate (D12), so the two screens can't drift on the rule.
// `null`/`undefined` (not yet resolved) defaults to eligible — callers decide whether to show the
// option while a sale is still loading; this only encodes the "never eligible" cases.
export const isPaymentSourceEligibleForInsuranceCredit = (
  paymentSourceType?: number | null
): boolean =>
  paymentSourceType !== PRIVATE_PAY_PAYMENT_SOURCE &&
  paymentSourceType !== MANAGED_CARE_PAYMENT_SOURCE;

export const QUICK_FILTER_DEFAULT_TYPE = "1";
export const QUICK_FILTER_READY_FOR_DELIVERY = "2";
export const QUICK_FILTER_UNCONFIRMED_APPOINTMENTS_TYPE = "3";
export const QUICK_FILTER_NO_SHOW_OR_CANCELED_APPOINTMENT = "5";
export const QUICK_FILTER_WAITLIST = "6";
export const QUICK_FILTER_INACTIVE = "7";
export const QUICK_FILTER_OUTSTANDING_BALANCE_TYPE = "8";
export const QUICK_FILTER_NEEDS_TO_BE_RESCHEDULED = "9";

const patientQuickFiltersType = {
  [QUICK_FILTER_DEFAULT_TYPE]: "Active Patients",
  [QUICK_FILTER_READY_FOR_DELIVERY]: "Patients Ready for Delivery",
  // Note: The 'Patients with Outstanding Balance' filter is currently hidden, but may be needed in the future.
  // [QUICK_FILTER_OUTSTANDING_BALANCE_TYPE]: "Patients with Outstanding Balance",
  [QUICK_FILTER_UNCONFIRMED_APPOINTMENTS_TYPE]:
    "Unconfirmed Appointments next 10 Days",
  [QUICK_FILTER_NO_SHOW_OR_CANCELED_APPOINTMENT]:
    "No-Show/Canceled Appointments",
  [QUICK_FILTER_WAITLIST]: "Waitlist",
  [QUICK_FILTER_INACTIVE]: "Deactivated Patients",
  [QUICK_FILTER_NEEDS_TO_BE_RESCHEDULED]: "Needs to be Rescheduled"
};

export const QUICK_FILTER_TYPES = new EnumDataSource(patientQuickFiltersType);

export const PHONE_TYPE_MOBILE = 1;
export const PHONE_TYPE_LANDLINE = 2;

const phoneTypes = {
  [PHONE_TYPE_MOBILE]: "Mobile",
  [PHONE_TYPE_LANDLINE]: "Landline"
};

export const PHONE_TYPES = new EnumDataSource(phoneTypes);

export const COMMUNICATION_METHOD_TEXT_SMS = 1;
export const COMMUNICATION_METHOD_EMAIL = 2;
export const COMMUNICATION_METHOD_VOICE_CALL = 3;
export const COMMUNICATION_METHOD_VOICE_DIRECT_MAIL = 4;
export const COMMUNICATION_METHOD_DO_NOT_CONTACT = 5;
export const COMMUNICATION_METHOD_FAX = 6;

export const BULK_ACTION_TARGET_TOUCHPOINTS_SMS = 3;
// Backend `BulkActionTarget` values (accounts/models/bulk_action.py). The history
// page and the completion toasts switch on these.
export const BULK_ACTION_TARGET_TASKS = 1;
export const BULK_ACTION_TARGET_TOUCHPOINTS = 2;
export const BULK_ACTION_TARGET_TAGS = 4;
export const BULK_ACTION_TARGET_PATIENTS_DEACTIVATE = 5;
export const BULK_ACTION_TARGET_BACKFILL_EDOC_FOLDERS = 6;
export const BULK_ACTION_TARGET_CATALOG_CSV_IMPORT = 7;
export const BULK_ACTION_TARGET_PATIENTS_PREFERRED_PROVIDER = 8;
export const BULK_ACTION_TARGET_PATIENTS_REFERRAL_SOURCE = 9;
export const BULK_ACTION_TARGET_QBO_SYNC_RETRY = 10;
export const BULK_ACTION_TARGET_PATIENTS_FIELD_UPDATE = 11;
export const BULK_ACTION_TARGET_TAGS_REMOVE = 12;
export const BULK_ACTION_TARGET_TAGS_MERGE = 13;
export const BULK_ACTION_TARGET_TAGS_CONVERT = 14;
export const BULK_ACTION_TARGET_TAGS_IMPORT = 15;
export const BULK_ACTION_TARGET_BULK_UNDO = 16;
// Administration > Clinics > "Move patients" (SN-19777)
export const BULK_ACTION_TARGET_PATIENTS_PREFERRED_CLINIC = 17;
// Administration > Users > "Move appointments to another provider" (SN-19777).
export const BULK_ACTION_TARGET_APPOINTMENTS_STAFF_MEMBER = 18;

const communicationMethods = {
  [COMMUNICATION_METHOD_TEXT_SMS]: "Text/SMS",
  [COMMUNICATION_METHOD_EMAIL]: "Email",
  [COMMUNICATION_METHOD_VOICE_CALL]: "Voice Call",
  [COMMUNICATION_METHOD_VOICE_DIRECT_MAIL]: "Direct Mail",
  [COMMUNICATION_METHOD_DO_NOT_CONTACT]: "Do Not Contact"
} as const;

export const communicationMethodsWithFax = {
  ...communicationMethods,
  [COMMUNICATION_METHOD_FAX]: "Fax"
} as const;

export const COMMUNICATION_METHOD_TYPES = new EnumDataSource(
  communicationMethods
);

export const COMMUNICATION_METHOD_TYPES_WITH_FAX = new EnumDataSource(
  communicationMethodsWithFax
);

// Methods selectable as a practice's default for new patients — excludes
// "Do Not Contact"/"Fax", which are not sensible defaults (ENG-1211).
const defaultPatientCommunicationMethods = {
  [COMMUNICATION_METHOD_TEXT_SMS]: "Text/SMS",
  [COMMUNICATION_METHOD_EMAIL]: "Email",
  [COMMUNICATION_METHOD_VOICE_CALL]: "Voice Call",
  [COMMUNICATION_METHOD_VOICE_DIRECT_MAIL]: "Direct Mail"
} as const;

export const DEFAULT_PATIENT_COMMUNICATION_METHOD_TYPES = new EnumDataSource(
  defaultPatientCommunicationMethods
);

export const COMMUNICATION_METHOD_BOTH_TEXT_AND_EMAIL = 5;
export const communicationMethodsGeneratedBy = {
  [COMMUNICATION_METHOD_TEXT_SMS]: "Text/SMS (Automated)",
  [COMMUNICATION_METHOD_EMAIL]: "Email (Automated)",
  [COMMUNICATION_METHOD_BOTH_TEXT_AND_EMAIL]:
    "Both Email and Text/SMS (Automated)",
  [COMMUNICATION_METHOD_VOICE_CALL]: "Voice Call (Manual)",
  [COMMUNICATION_METHOD_VOICE_DIRECT_MAIL]: "Direct Mail (Manual)",
  [COMMUNICATION_METHOD_FAX]: "Fax (Manual)"
} as const;

export const COMMUNICATION_METHOD_TYPES_GENERATED_BY = new EnumDataSource(
  communicationMethodsGeneratedBy,
  //options order
  {
    [COMMUNICATION_METHOD_TEXT_SMS]: 1,
    [COMMUNICATION_METHOD_EMAIL]: 2,
    [COMMUNICATION_METHOD_BOTH_TEXT_AND_EMAIL]: 3,
    [COMMUNICATION_METHOD_VOICE_CALL]: 4,
    [COMMUNICATION_METHOD_VOICE_DIRECT_MAIL]: 5,
    [COMMUNICATION_METHOD_FAX]: 6
  }
);

export const CRM_SEGMENT_PROSPECT = 1;
export const CRM_SEGMENT_NOT_CONVERTED = 2;
export const CRM_SEGMENT_HA_USER = 3;
export const CRM_SEGMENT_END_OF_WARRANTY = 4;

const crmSegments = {
  [CRM_SEGMENT_PROSPECT]: "Prospect",
  [CRM_SEGMENT_NOT_CONVERTED]: "Not Converted",
  [CRM_SEGMENT_HA_USER]: "HA User",
  [CRM_SEGMENT_END_OF_WARRANTY]: "End Of Warranty"
};

export const CRM_SEGMENTS = new EnumDataSource(crmSegments);

export const NOAH_ES = 1;
export const NOAH_MOBILE = 2;
export const PATIENTS_LIST_KEY_DEFAULT = "patientsList";

// SN-19556. Mirrors `Patient.secondary_preferred_provider_role`'s max_length on the backend;
// the field is free text, so this is the only thing bounding it.
export const SECONDARY_PROVIDER_ROLE_MAX_LENGTH = 100;

/**
 * Patients report "Age Range" filter buckets. Values are the `age_range` API tokens
 * accepted by the backend PatientFilterSet (suno_backend/patients/utils/age_ranges.py) —
 * keep the two lists in step, and never rename a token (scheduled sends store them).
 * Split at 18 so "adults" and "pediatric" are selectable; `100-plus`, not `100+`,
 * because a `+` in a query string decodes to a space. Labels use a plain hyphen so
 * typing "30-39" into the dropdown's search finds the bucket. "Unknown age" also
 * covers a future date of birth.
 */
export const AGE_RANGE_UNKNOWN = "unknown";

export const AGE_RANGE_OPTIONS: { value: string; label: string }[] = [
  { value: "0-17", label: "0-17" },
  { value: "18-29", label: "18-29" },
  { value: "30-39", label: "30-39" },
  { value: "40-49", label: "40-49" },
  { value: "50-59", label: "50-59" },
  { value: "60-69", label: "60-69" },
  { value: "70-79", label: "70-79" },
  { value: "80-89", label: "80-89" },
  { value: "90-99", label: "90-99" },
  { value: "100-plus", label: "100+" },
  { value: AGE_RANGE_UNKNOWN, label: "Unknown age" }
];

/** `age_range` API tokens → the labels the chip shows (falls back to the raw token). */
export const ageRangeLabel = (value: string): string =>
  AGE_RANGE_OPTIONS.find((option) => option.value === value)?.label ?? value;

/**
 * Chip text for a set of age buckets. Adjacent buckets collapse into one span, so
 * picking every adult range reads "18+" rather than "9 selected".
 */
export const ageRangeChipLabel = (
  value: Array<{ value: string }> | null | undefined
): string | null => {
  if (!value?.length) return null;
  const selected = new Set(value.map((option) => option.value));
  const spans: string[] = [];
  let runStart: string | null = null;
  let runEnd: string | null = null;
  const flush = () => {
    if (runStart === null || runEnd === null) return;
    const from = runStart.split("-")[0];
    const to = runEnd.split("-")[1];
    spans.push(to === "plus" ? `${from}+` : `${from}-${to}`);
    runStart = null;
    runEnd = null;
  };
  AGE_RANGE_OPTIONS.forEach((option) => {
    if (option.value === AGE_RANGE_UNKNOWN) return;
    if (selected.has(option.value)) {
      runStart = runStart ?? option.value;
      runEnd = option.value;
    } else {
      flush();
    }
  });
  flush();
  if (selected.has(AGE_RANGE_UNKNOWN))
    spans.push(ageRangeLabel(AGE_RANGE_UNKNOWN));
  return spans.length ? spans.join(", ") : null;
};
